import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { usersAPI } from '../../api/users'
import DashIcon from '../../components/ui/DashIcon'
import DashAvatar from '../../components/ui/DashAvatar'

// ─── Types ─────────────────────────────────────────────────────────────────────

interface LivreurRow {
  _id: string
  name: string
  phone: string
  vehicle?: { type?: string; brand?: string; model?: string; plate?: string }
  isOnline: boolean
  isVerified: boolean
  isSuspended?: boolean
  rating: number
  totalDeliveries: number
  commissionFixe?: number
  createdAt: string
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

const VEHICLE_LABEL: Record<string, string> = {
  moto: 'Moto', voiture: 'Voiture', camionnette: 'Camionnette', camion: 'Camion',
}

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString('fr-TN', { day: '2-digit', month: 'short', year: 'numeric' })

// ─── Component ─────────────────────────────────────────────────────────────────

export default function AdminLivreurs() {
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [page, setPage]           = useState(1)
  const [searchInput, setSearch]  = useState('')
  const [search, setDebouncedSearch] = useState('')
  const [filterOnline, setFilterOnline]     = useState<boolean | undefined>(undefined)
  const [filterVerified, setFilterVerified] = useState<boolean | undefined>(undefined)

  const PAGE_SIZE = 15

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(searchInput); setPage(1) }, 350)
    return () => clearTimeout(t)
  }, [searchInput])

  const { data, isLoading } = useQuery({
    queryKey: ['livreurs', page, search, filterOnline, filterVerified],
    queryFn: () => usersAPI.getLivreurs({
      page,
      limit: PAGE_SIZE,
      search: search || undefined,
      isOnline: filterOnline,
      isVerified: filterVerified,
    }),
    refetchInterval: 30_000,
  })

  const rows: LivreurRow[] = (data as any)?.data?.data ?? (data as any)?.data ?? []
  const total      = (data as any)?.data?.total ?? 0
  const totalPages = Math.ceil(total / PAGE_SIZE) || 1

  const verifyMut = useMutation({
    mutationFn: (id: string) => usersAPI.verifyLivreur(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['livreurs'] }); toast.success('Livreur vérifié') },
    onError: () => toast.error('Erreur lors de la vérification'),
  })

  const suspendMut = useMutation({
    mutationFn: ({ id, suspended }: { id: string; suspended: boolean }) =>
      usersAPI.setSuspended(id, suspended),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['livreurs'] }); toast.success('Statut mis à jour') },
    onError: () => toast.error('Erreur'),
  })

  return (
    <>
      <div className="page-head">
        <div className="ph-titles">
          <h1>Livreurs</h1>
          <p>Gestion des livreurs, vérification et commissions.</p>
        </div>
        <div className="ph-actions">
          <button className="btn btn-ghost"><DashIcon name="download" size={14} /> Exporter</button>
        </div>
      </div>

      <div className="tbl-card">
        {/* Toolbar */}
        <div className="tbl-toolbar">
          <div className="tbl-search">
            <DashIcon name="search" size={14} />
            <input
              placeholder="Nom, téléphone…"
              value={searchInput}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {/* Filter: online */}
          <select
            className="btn btn-sm btn-ghost"
            value={filterOnline === undefined ? '' : String(filterOnline)}
            onChange={e => {
              setFilterOnline(e.target.value === '' ? undefined : e.target.value === 'true')
              setPage(1)
            }}
            style={{ cursor: 'pointer' }}
          >
            <option value="">Tous les statuts</option>
            <option value="true">En ligne</option>
            <option value="false">Hors ligne</option>
          </select>

          {/* Filter: verified */}
          <select
            className="btn btn-sm btn-ghost"
            value={filterVerified === undefined ? '' : String(filterVerified)}
            onChange={e => {
              setFilterVerified(e.target.value === '' ? undefined : e.target.value === 'true')
              setPage(1)
            }}
            style={{ cursor: 'pointer' }}
          >
            <option value="">Tous</option>
            <option value="true">Vérifiés</option>
            <option value="false">Non vérifiés</option>
          </select>
        </div>

        {/* Table */}
        <div className="tbl-scroll">
          <table className="tbl">
            <thead>
              <tr>
                <th>Livreur</th>
                <th>Véhicule</th>
                <th>Note</th>
                <th>Livraisons</th>
                <th>Commission</th>
                <th>Statut</th>
                <th>Inscription</th>
                <th className="col-actions"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', color: 'var(--muted)', padding: 40 }}>
                    Chargement…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', color: 'var(--muted)', padding: 40 }}>
                    Aucun livreur trouvé.
                  </td>
                </tr>
              ) : rows.map((l) => (
                <tr key={l._id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/admin/livreurs/${l._id}`)}>
                  <td>
                    <div className="cell-user">
                      <DashAvatar name={l.name} size={32} />
                      <div>
                        <b>{l.name}</b>
                        <span>{l.phone}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    {l.vehicle?.type
                      ? <span>{VEHICLE_LABEL[l.vehicle.type] ?? l.vehicle.type}{l.vehicle.plate ? ` · ${l.vehicle.plate}` : ''}</span>
                      : <span style={{ color: 'var(--muted)' }}>—</span>
                    }
                  </td>
                  <td>
                    {l.rating > 0
                      ? <span>{'★ '}{l.rating.toFixed(1)}</span>
                      : <span style={{ color: 'var(--muted)' }}>—</span>
                    }
                  </td>
                  <td>{l.totalDeliveries ?? 0}</td>
                  <td>
                    <span style={{ fontWeight: 600, color: '#1D9E75' }}>
                      {l.commissionFixe ?? 3} TND
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{
                        padding: '2px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600,
                        background: l.isOnline ? '#D1FAE5' : '#F3F4F6',
                        color: l.isOnline ? '#065F46' : '#6B7280',
                      }}>
                        {l.isOnline ? 'En ligne' : 'Hors ligne'}
                      </span>
                      {!l.isVerified && (
                        <span style={{
                          padding: '2px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600,
                          background: '#FEF3C7', color: '#92400E',
                        }}>Non vérifié</span>
                      )}
                      {l.isSuspended && (
                        <span style={{
                          padding: '2px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600,
                          background: '#FEE2E2', color: '#991B1B',
                        }}>Suspendu</span>
                      )}
                    </div>
                  </td>
                  <td style={{ color: 'var(--muted)', fontSize: 12 }}>{fmtDate(l.createdAt)}</td>
                  <td className="col-actions" onClick={e => e.stopPropagation()}>
                    {!l.isVerified && (
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => verifyMut.mutate(l._id)}
                        disabled={verifyMut.isPending}
                      >
                        Vérifier
                      </button>
                    )}
                    <button
                      className="btn btn-sm btn-ghost"
                      onClick={() => navigate(`/admin/livreurs/${l._id}`)}
                    >
                      Voir
                    </button>
                    <button
                      className="btn btn-sm btn-ghost"
                      style={{ color: l.isSuspended ? '#1D9E75' : '#EF4444' }}
                      onClick={() => suspendMut.mutate({ id: l._id, suspended: !l.isSuspended })}
                      disabled={suspendMut.isPending}
                    >
                      {l.isSuspended ? 'Réactiver' : 'Suspendre'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={{
          padding: '14px 18px', borderTop: '1px solid var(--line)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          fontSize: '.82rem', color: 'var(--muted)',
        }}>
          <span>
            {total > 0
              ? `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} sur ${total} livreurs`
              : 'Aucun livreur'}
          </span>
          {totalPages > 1 && (
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-sm btn-ghost" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>←</button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map(p => (
                <button key={p} className={`btn btn-sm ${page === p ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button className="btn btn-sm btn-ghost" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>→</button>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
